'use client';

import { m } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import { useBoolean } from 'src/hooks/use-boolean';
import { useNotifications } from 'src/hooks/use-notifications';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';

import { NotificationItem } from './notification-item';

export function NotificationsDrawer({ sx, ...other }) {
  const drawer = useBoolean();
  const [currentTab, setCurrentTab] = useState('all');

  const {
    notifications,
    stats,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
  } = useNotifications({ limit: 50, autoRefresh: true, refreshInterval: 60000 });

  // Refresh notifications when drawer opens
  useEffect(() => {
    if (drawer.value) {
      refresh();
    }
  }, [drawer.value, refresh]);

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotification(id);
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  // Filter notifications based on current tab
  const filteredNotifications = notifications.filter((notification) => {
    if (currentTab === 'all') return true;
    if (currentTab === 'unread') return notification.isUnRead;
    if (currentTab === 'archived') return !notification.isUnRead;
    return true;
  });

  const TABS = [
    { value: 'all', label: 'All', count: stats.total },
    { value: 'unread', label: 'Unread', count: stats.unread },
    { value: 'archived', label: 'Archived', count: stats.archived },
  ];

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!stats.unread && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Refresh">
        <IconButton onClick={refresh} disabled={loading}>
          <Iconify icon="eva:refresh-fill" />
        </IconButton>
      </Tooltip>

      <IconButton onClick={drawer.onFalse} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>

      <IconButton>
        <Iconify icon="solar:settings-bold-duotone" />
      </IconButton>
    </Stack>
  );

  const renderTabs = (
    <CustomTabs variant="fullWidth" value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </CustomTabs>
  );

  const renderLoading = (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
      <CircularProgress size={32} />
    </Stack>
  );

  const renderError = (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
      <Typography color="error" variant="body2">
        {error || 'Failed to load notifications'}
      </Typography>
      <Button onClick={refresh} sx={{ mt: 2 }}>
        Retry
      </Button>
    </Stack>
  );

  const renderEmpty = (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
      <Iconify icon="eva:bell-off-outline" width={48} sx={{ color: 'text.disabled', mb: 2 }} />
      <Typography color="text.disabled" variant="body2">
        No notifications yet
      </Typography>
    </Stack>
  );

  const renderList = (
    <Scrollbar>
      <Box component="ul">
        {filteredNotifications.map((notification) => (
          <Box component="li" key={notification.id} sx={{ display: 'flex' }}>
            <NotificationItem
              notification={notification}
              onMarkAsRead={() => handleMarkAsRead(notification.id)}
              onDelete={() => handleDelete(notification.id)}
            />
          </Box>
        ))}
      </Box>
    </Scrollbar>
  );

  const renderContent = () => {
    if (loading && notifications.length === 0) return renderLoading;
    if (error && notifications.length === 0) return renderError;
    if (filteredNotifications.length === 0) return renderEmpty;
    return renderList;
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={drawer.onTrue}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={stats.unread} color="error">
          <SvgIcon>
            {/* https://icon-sets.iconify.design/solar/bell-bing-bold-duotone/ */}
            <path
              fill="currentColor"
              d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
              opacity="0.5"
            />
            <path
              fill="currentColor"
              d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
            />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead}

        {renderTabs}

        {renderContent()}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large" onClick={drawer.onFalse}>
            Close
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
